// French labels for the reconciliation screens — the only French admin screens (Bruno, 2026-10-16). The
// backend sends codes; each map follows the contract's Vocabularies / Flags tables
// (docs/api-types/admin-reconciliation.md), and every lookup falls back to the raw code so a new backend
// code never renders blank. Mollie's own strings (cost-line descriptions, invoice lines) are English data
// and are shown as is, never through these maps.

type LabelMap = Readonly<Record<string, string>>;

/** The label of `code` in `map`, the raw code when unknown, `—` when null. */
export function labelOf(map: LabelMap, code: string | null | undefined): string {
  if (code === null || code === undefined || code === '') {
    return '—';
  }
  return Object.prototype.hasOwnProperty.call(map, code) ? map[code] : code;
}

export const PURPOSE_LABELS: LabelMap = {
  invoice: 'Paiement de facture',
  domain_order: 'Commande de domaine',
  wallet_topup: 'Rechargement du solde (acompte)',
  card_setup: 'Enregistrement de carte',
  unknown: 'Hors Manager',
};

export const COST_CLASS_LABELS: LabelMap = {
  fee: 'Frais de transaction',
  rounding: "Écart d'arrondi",
  compensation: 'Compensation de facture',
  discount: 'Remise sur frais',
  other: 'Autre',
};

/** Display order of the cost classes (the `by_class` / `costs_by_class` objects). */
export const COST_CLASS_ORDER = ['fee', 'rounding', 'compensation', 'discount', 'other'] as const;

export const ORIGIN_LABELS: LabelMap = {
  domain_order: 'Commande de domaine',
  gpu_usage: 'Consommation GPU',
  billing: 'Facturation récurrente',
  other: 'Autre',
};

export const SETTLED_VIA_LABELS: LabelMap = {
  mollie: 'Payée via Mollie',
  mollie_prepaid: 'Prépayée via Mollie (commande)',
  wallet: 'Payée par le solde prépayé',
  wallet_prepaid: 'Prépayée par le solde',
  wallet_pending: 'Paiement par solde à régulariser',
  outside_manager: 'Payée hors Manager',
  none: 'Non payée',
  credit_note: 'Note de crédit',
};

/** Facturier's `paye` status. */
export const PAYE_LABELS: LabelMap = {
  oui: 'Payée',
  non: 'Non payée',
  credite: 'Créditée',
};

/** Facturier's document `type`. */
export const SALES_TYPE_LABELS: LabelMap = {
  officielle: 'Facture',
  'note de credit': 'Note de crédit',
};

/** Mollie balance-transaction types (open-ended: 48 documented, unknown ones shown as is). */
export const TRANSACTION_TYPE_LABELS: LabelMap = {
  payment: 'Paiement',
  refund: 'Remboursement',
  chargeback: 'Rétrofacturation',
  'chargeback-reversal': 'Annulation de rétrofacturation',
  'outgoing-transfer': 'Versement',
  'incoming-transfer': 'Virement entrant',
  'invoice-compensation': 'Compensation de facture',
  capture: 'Capture',
  'failed-payment': 'Paiement échoué',
  'balance-correction': 'Correction de solde',
  'returned-transfer': 'Versement retourné',
  'refund-compensation': 'Compensation de remboursement',
  'returned-refund': 'Remboursement retourné',
};

export const DIRECTION_LABELS: LabelMap = {
  credit: 'Crédit',
  debit: 'Débit',
};

/** Mollie's balance-report categories (open-ended). */
export const REPORT_CATEGORY_LABELS: LabelMap = {
  payments: 'Paiements',
  refunds: 'Remboursements',
  chargebacks: 'Rétrofacturations',
  capital: 'Capital',
  transfers: 'Versements',
  'fee-prepayments': 'Frais prélevés',
  corrections: 'Corrections',
  topups: 'Recharges du solde Mollie',
};

export const SETTLEMENT_STATUS_LABELS: LabelMap = {
  paidout: 'Versé',
  pending: 'En attente',
  processing: 'En traitement',
  'processing-at-bank': 'En traitement à la banque',
  failed: 'Échoué',
  open: 'Non versé',
};

export const MOLLIE_INVOICE_STATUS_LABELS: LabelMap = {
  open: 'Ouverte',
  paid: 'Payée',
  overdue: 'En retard',
};

/** Kinds of the items a payout carried (Show Settlement `lines[].kind`). */
export const SETTLEMENT_LINE_KIND_LABELS: LabelMap = {
  payment: 'Paiement',
  refund: 'Remboursement',
  chargeback: 'Rétrofacturation',
  capture: 'Capture',
};

export const SOURCE_LABELS: LabelMap = {
  checkout: 'Paiement en ligne',
  card: 'Carte enregistrée',
  autopay: 'Prélèvement automatique',
};

export const MATCHED_BY_LABELS: LabelMap = {
  local: 'Ligne du Manager',
  metadata: 'Métadonnées Mollie',
};

export const DOMAIN_ORDER_TYPE_LABELS: LabelMap = {
  registration: 'Enregistrement',
  transfer: 'Transfert',
  trade: 'Changement de titulaire',
};

export const DOMAIN_ORDER_STATUS_LABELS: LabelMap = {
  awaiting_payment: 'En attente de paiement',
  completed: 'Terminée',
  partially_failed: 'Partiellement échouée',
  failed: 'Échouée',
};

export const PAYMENT_METHOD_LABELS: LabelMap = {
  creditcard: 'Carte de crédit',
  bancontact: 'Bancontact',
  kbc: 'KBC/CBC',
  belfius: 'Belfius',
  banktransfer: 'Virement',
  directdebit: 'Domiciliation',
  ideal: 'iDEAL',
  applepay: 'Apple Pay',
  paypal: 'PayPal',
};

export const WALLET_KIND_LABELS: LabelMap = {
  deposit: 'Rechargement',
  charge: 'Débit',
  reversal: 'Annulation',
  clawback: 'Reprise (remboursement Mollie)',
  adjustment: 'Ajustement (staff)',
};

export const WALLET_REFERENCE_TYPE_LABELS: LabelMap = {
  facturier_invoice: 'Facture',
  domain_order: 'Commande de domaine',
  gpu_server_usage: 'Consommation GPU',
  wallet_deposit: 'Rechargement',
};

export const TRANSFER_FREQUENCY_LABELS: LabelMap = {
  daily: 'Quotidien',
  'every-monday': 'Chaque lundi',
  'every-tuesday': 'Chaque mardi',
  'every-wednesday': 'Chaque mercredi',
  'every-thursday': 'Chaque jeudi',
  'every-friday': 'Chaque vendredi',
  monthly: 'Mensuel',
  never: 'Jamais',
};

// ── Accounting entries (the « Comptabilité » tab) ──

export const ENTRY_KIND_LABELS: LabelMap = {
  mollie_payout: 'Versement Mollie (extrait)',
  mollie_payout_od: 'Versement Mollie (OD)',
  mollie_invoice: 'Facture Mollie',
  sales_invoice: 'Facture de vente',
  credit_note: 'Note de crédit',
  wallet_payment: 'Paiement par portefeuille',
  wallet_reversal: "Annulation d'un paiement par portefeuille",
  gpu_usage: 'Consommation GPU',
  wallet_adjustment: 'Ajustement du portefeuille',
};

export const JOURNAL_LABELS: LabelMap = {
  bank: 'Financier — banque',
  purchases: 'Achats',
  sales: 'Ventes',
  misc: 'Opérations diverses',
};

export const ENTRY_STATUS_LABELS: LabelMap = {
  to_encode: 'À encoder',
  to_check: 'À vérifier',
};

export const ENTRY_STATUS_MEANINGS: LabelMap = {
  to_encode:
    "Pas connue de WinAuditor : à encoder, sauf si elle a déjà été comptabilisée autrement (document antérieur à l'export WinAuditor de Facturier, ou encodé à la main).",
  to_check: 'Devrait déjà être dans WinAuditor (exportée par Facturier, ou reçue via PEPPOL) : à vérifier.',
};

export const WINAUDITOR_STATUS_LABELS: LabelMap = {
  sent: 'Exportée vers WinAuditor',
  test: 'Export de test seulement',
  failed: 'Export WinAuditor en échec',
};

/** The chart-of-accounts roles (`description` comes from the backend, in French). */
export const ACCOUNT_ROLE_LABELS: LabelMap = {
  bank: 'Banque',
  mollie_transit: 'Transit des versements Mollie',
  customers: 'Clients',
  wallet: 'Portefeuilles clients',
  mollie_supplier: 'Mollie (fournisseur)',
  payment_fees: 'Frais de paiement',
  rounding_expense: "Différences d'arrondi (débit)",
  rounding_income: "Différences d'arrondi (crédit)",
  vat_due: 'TVA à payer',
  vat_deductible: 'TVA à récupérer',
  revenue_default: 'Ventes (compte par défaut)',
  suspense: "Compte d'attente",
};

/** How WinAuditor encodes an account (TYCMP of the OD import file). */
export const WINAUDITOR_TYPE_LABELS: LabelMap = {
  G: 'Compte général',
  C: 'Client',
  F: 'Fournisseur',
};

export interface FlagInfo {
  label: string;
  /** What to look at — shown as the flag's tooltip. */
  meaning: string;
}

/** Journal / settlement-line flags and sales flags (`collected_more_than_once` is shared). */
export const FLAG_INFO: Readonly<Record<string, FlagInfo>> = {
  unattributed: {
    label: 'Paiement inconnu du Manager',
    meaning: "Un paiement Mollie que le Manager n'a pas créé (tableau de bord Mollie, lien de paiement…).",
  },
  refunded: {
    label: 'Remboursé chez Mollie',
    meaning:
      'Mollie a remboursé (une partie de) ce paiement. Pour une facture ou une commande de domaine, la facture reste payée dans Facturier : une note de crédit peut être due.',
  },
  charged_back: {
    label: 'Rétrofacturé',
    meaning:
      'La banque du client a rétrofacturé ce paiement. Même conséquence que le remboursement : une note de crédit peut être due.',
  },
  invoice_not_marked_paid: {
    label: 'Facture non marquée payée',
    meaning:
      "Mollie a encaissé l'argent mais le Manager n'a jamais marqué la facture payée dans Facturier (elle avait par exemple été créditée entre-temps).",
  },
  order_refund_owed: {
    label: 'Remboursement dû (commande)',
    meaning:
      "Une commande de domaine payée via Mollie a échoué : de l'argent est dû au client. Une commande échouée reste signalée jusqu'à ce que Mollie ait remboursé le montant total ; une commande partiellement échouée reste signalée (vérifiez « Remboursé chez Mollie » à côté).",
  },
  order_not_invoiced: {
    label: 'Commande non facturée',
    meaning: "Une commande de domaine terminée n'a pas encore de facture (facturation en attente).",
  },
  collected_more_than_once: {
    label: 'Encaissée plusieurs fois',
    meaning:
      "La facture a été encaissée plus d'une fois (deux tentatives payées, ou une tentative payée sur une facture de commande prépayée) : de l'argent est à rendre.",
  },
  manager_paid_not_in_facturier: {
    label: 'Encaissée mais non soldée dans Facturier',
    meaning: "Le Manager a encaissé l'argent mais Facturier indique toujours « non payée ».",
  },
  refunded_at_mollie: {
    label: 'Remboursée chez Mollie',
    meaning: 'Le paiement Mollie de cette facture a été remboursé depuis.',
  },
  charged_back_at_mollie: {
    label: 'Rétrofacturée chez Mollie',
    meaning: 'Le paiement Mollie de cette facture a été rétrofacturé depuis.',
  },
  amount_mismatch: {
    label: 'Montant différent',
    meaning:
      'Le montant encaissé par le Manager diffère du total de la facture (commande partiellement échouée, double encaissement…).',
  },
  order_waived: {
    label: 'Commande offerte (non encaissée)',
    meaning: "Commande de domaine offerte par le staff : rien n'a été encaissé, la facture reste à payer.",
  },
  collected_not_recorded: {
    label: 'Encaissée chez Mollie, non enregistrée',
    meaning:
      "Mollie a encaissé l'argent via une tentative que le Manager n'a jamais enregistrée comme payée (facture créditée entre-temps, ou confirmation perdue).",
  },
  paid_on_differs_from_mollie: {
    label: 'Date de paiement décalée',
    meaning:
      "La date de paiement de Facturier n'est pas la date bruxelloise du paiement Mollie (facture estampillée avec la date UTC avant le 16/10/2026, ou date modifiée à la main dans Facturier).",
  },
};

/** Flags that mean money is owed back — rendered in red rather than orange. */
export const MONEY_OWED_FLAGS: ReadonlySet<string> = new Set([
  'order_refund_owed',
  'collected_more_than_once',
  'collected_not_recorded',
]);

/** Accounting-entry flags (`entries[].flags`) — some codes mean something else than on journal rows. */
export const ENTRY_FLAG_INFO: Readonly<Record<string, FlagInfo>> = {
  unattributed: {
    label: 'Paiement non attribué',
    meaning: "Le versement porte un paiement Mollie que le Manager n'a pas créé : comptabilisé en compte d'attente.",
  },
  refund: { label: 'Remboursement', meaning: 'Le versement a déduit un remboursement.' },
  chargeback: { label: 'Rétrofacturation', meaning: 'Le versement a déduit une rétrofacturation.' },
  unexplained_difference: {
    label: 'Écart inexpliqué',
    meaning:
      "Les lignes n'expliquent pas tout le montant : une ligne « Écart inexpliqué » en compte d'attente l'équilibre.",
  },
  reverse_charge: {
    label: 'Autoliquidation',
    meaning: 'La facture Mollie est en autoliquidation : TVA autoliquidée (grilles 82 + 88 / 55 / 59).',
  },
  revenue_account_missing: {
    label: 'Compte de vente manquant',
    meaning: "La catégorie Facturier d'une ligne n'a pas de compte : comptabilisée sur le compte de vente par défaut.",
  },
  ventilation_estimated: {
    label: 'Ventilation estimée',
    meaning:
      "Facturier n'a pas envoyé de ventilation de recettes ou de TVA exploitable : une ligne sur le compte par défaut et le total de TVA.",
  },
  ventilation_mismatch: {
    label: 'Ventilation incohérente',
    meaning:
      "La ventilation ne correspond pas aux totaux du document : un reliquat par rapport au TVAC est en compte d'attente ; une ventilation qui correspond au TVAC mais pas aux HTVA / TVA enregistrés (ancien document à la TVA périmée) est signalée sans ligne d'attente.",
  },
  winauditor_status_unknown: {
    label: 'Statut WinAuditor inconnu',
    meaning: "Facturier n'indique pas si le document est arrivé dans WinAuditor : à vérifier.",
  },
  winauditor_export_failed: {
    label: 'Export WinAuditor en échec',
    meaning: "L'export du document vers WinAuditor a échoué dans Facturier : à encoder.",
  },
  counterpart_to_choose: {
    label: 'Contrepartie à choisir',
    meaning:
      "Comptabilisée contre le compte d'attente : choisissez la contrepartie (geste commercial, remboursement par virement…).",
  },
  winauditor_code_missing: {
    label: 'Code client WinAuditor manquant',
    meaning:
      "L'OD nomme un client dont le code WinAuditor n'est pas enregistré : saisissez-le (« Codes clients WinAuditor ») pour pouvoir l'exporter.",
  },
  gpu_period_estimated: {
    label: 'Période GPU estimée',
    meaning:
      "Une ligne d'usage d'une heure GPU manque (ou sa période est illisible) : sa période est le mois UTC du débit du portefeuille. Vérifiez-la avec la facture GPU ; le montant est comptabilisé quand même.",
  },
};

/** Entry flags that need an action before encoding — rendered in red rather than orange. */
export const ENTRY_ACTION_FLAGS: ReadonlySet<string> = new Set([
  'unexplained_difference',
  'winauditor_export_failed',
  'counterpart_to_choose',
  'winauditor_code_missing',
]);

/** Entry flags as French labels joined for a CSV cell (`""` when none). */
export function entryFlagsText(flags: string[]): string {
  return flags.map((f) => ENTRY_FLAG_INFO[f]?.label ?? f).join(', ');
}

export function flagLabel(code: string): string {
  return FLAG_INFO[code]?.label ?? code;
}

export function flagMeaning(code: string): string | null {
  return FLAG_INFO[code]?.meaning ?? null;
}

/** Flags as French labels joined for a CSV cell (`""` when none). */
export function flagsText(flags: string[]): string {
  return flags.map(flagLabel).join(', ');
}

/** The fixed English upstream / input messages of the contract's Errors tables, in French. */
const ERROR_MESSAGES: LabelMap = {
  'Mollie reporting is not configured on this server':
    "Le reporting Mollie n'est pas configuré sur ce serveur (jeton MOLLIE_REPORTING_TOKEN manquant ou invalide).",
  'Mollie rejected the reporting token': 'Mollie a refusé le jeton de reporting (jeton erroné ou révoqué).',
  'The Mollie reporting token lacks a permission (or is restricted to one profile)':
    'Il manque une permission au jeton de reporting Mollie (ou il est limité à un seul profil).',
  'Mollie rate limit reached, please retry in a minute':
    'Limite de requêtes Mollie atteinte : réessayez dans une minute.',
  'Mollie is temporarily unreachable, please retry later':
    'Mollie est momentanément injoignable : réessayez plus tard.',
  'Mollie refused the request': 'Mollie a refusé la requête.',
  'Mollie returned an unexpected response': 'Mollie a renvoyé une réponse inattendue.',
  'An upstream service returned an unexpected response': 'Un service externe a renvoyé une réponse inattendue.',
  'Settlement not found': 'Versement introuvable.',
  'The Facturier invoice journal is not available (Facturier needs GET /manager-api/invoices)':
    "Le journal des factures de Facturier n'est pas disponible (Facturier doit exposer GET /manager-api/invoices).",
  'Could not reach the billing service. Please try again.':
    'Impossible de joindre le service de facturation. Réessayez.',
  'Could not read the sales invoices from the billing service':
    'Impossible de lire les factures de vente depuis le service de facturation.',
  'Could not read the Manager ledger': 'Impossible de lire le grand livre du Manager.',
  'Could not read the chart of accounts': 'Impossible de lire le plan comptable.',
  'Could not save the chart of accounts. Please try again.':
    "Impossible d'enregistrer le plan comptable (rien n'a été modifié). Réessayez.",
  'Could not read the WinAuditor customer codes': 'Impossible de lire les codes clients WinAuditor.',
  'Could not save the WinAuditor customer codes. Please try again.':
    "Impossible d'enregistrer les codes clients WinAuditor (rien n'a été modifié). Réessayez.",
  'This entry cannot be imported into WinAuditor': 'Cette écriture ne peut pas être importée dans WinAuditor.',
  'Some Mollie payments of this payout could not be read back, please retry before exporting it':
    "Certains paiements Mollie de ce versement n'ont pas pu être relus : actualisez avant de l'exporter.",
  'Could not build the WinAuditor file': 'Impossible de construire le fichier WinAuditor.',
  'Request contains errors': 'La requête contient des erreurs.',
  'number must be 3 to 10 digits': 'Le numéro doit compter de 3 à 10 chiffres.',
  'label must be 1 to 80 characters, without control characters':
    "L'intitulé doit compter de 1 à 80 caractères, sans caractère de contrôle.",
  'only a supplier role has a third-party code (mollie_supplier)':
    'Seul un rôle fournisseur (Mollie) a un code fournisseur WinAuditor.',
  'third_party_code must be 1 to 10 digits or letters (or null for the default)':
    'Le code fournisseur doit compter de 1 à 10 chiffres ou lettres.',
  'the code must be a string of 1 to 10 digits or letters (e.g. "0000004"), or null to forget it':
    'Le code doit compter de 1 à 10 chiffres ou lettres (par exemple 0000004).',
  'the key must be a Facturier client id (a positive integer)':
    'La clé doit être un identifiant de client Facturier (un entier positif).',
  "entry is required (the id of an OD of the month's accounting entries)":
    "L'écriture à exporter est requise (l'identifiant d'une OD des écritures du mois).",
  'month is required (YYYY-MM)': 'Le mois est requis (AAAA-MM).',
  'expected YYYY-MM': 'Mois attendu au format AAAA-MM.',
  'month is in the future': 'Ce mois est dans le futur.',
  'year is required (YYYY, not in the future)': "L'année est requise (AAAA, pas dans le futur).",
  'expected a Mollie settlement id (stl_…)': 'Identifiant de versement Mollie attendu (stl_…).',
  'basis must be issued or paid': 'La base doit être la date de facture ou la date de paiement.',
};

/** A backend error message in French (the raw message when the contract does not list it). */
export function errorMessageText(message: string): string {
  if (Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, message)) {
    return ERROR_MESSAGES[message];
  }
  if (message.startsWith('No change supplied')) {
    return 'Aucune modification à enregistrer.';
  }
  if (message.startsWith('Non-existent route')) {
    return 'Route inexistante.';
  }
  if (message.startsWith("No such entry in this month's accounting entries")) {
    return message.includes('the view is incomplete')
      ? 'Écriture introuvable dans les écritures du mois (la vue était incomplète : actualisez puis réessayez).'
      : 'Écriture introuvable dans les écritures du mois : actualisez la vue.';
  }
  return message;
}

/** The « not an OD » problem — it holds a `; ` of its own, so it is matched before the split. */
const NOT_AN_OD_RE = /only an OD \(journal misc\) can be imported this way; this entry belongs to the (\S+) journal/g;

/** Why the backend refuses to export an OD (`data.entry`, problems joined with `; `), in French where known. */
export function winauditorExportProblemText(detail: string): string {
  return detail
    .replace(NOT_AN_OD_RE, (_match, journal: string) => `not-an-od:${journal}`)
    .split('; ')
    .map((problem) => {
      if (problem.startsWith('WinAuditor customer code missing for: ')) {
        return `Code client WinAuditor manquant pour : ${problem.slice('WinAuditor customer code missing for: '.length)}`;
      }
      if (problem.startsWith('not-an-od:')) {
        const journal = labelOf(JOURNAL_LABELS, problem.slice('not-an-od:'.length));
        return `Seule une opération diverse (OD) peut être importée de cette façon ; cette écriture est au journal « ${journal} ».`;
      }
      if (problem === 'the entry does not balance') {
        return "L'écriture n'est pas équilibrée.";
      }
      if (problem === 'the entry has no line') {
        return "L'écriture n'a aucune ligne.";
      }
      return errorMessageText(problem);
    })
    .join(' ; ');
}
